import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/Icon';
import { brands, journey } from '../data';

const IMG = `${process.env.PUBLIC_URL}/images`;
const JIMG = `${IMG}/Journey`;

// Same milestones as the home page, with a rotating accent color per node.
const milestoneColors = [
  '#ec4899',
  '#ec4899',
  '#a855f7',
  '#a855f7',
  '#38bdf8',
  '#38bdf8',
  '#d9a94e',
  '#22c55e',
  '#22c55e',
];

const milestones = journey.map((m, i) => ({
  ...m,
  color: milestoneColors[i % milestoneColors.length],
}));

const lives = [
  { icon: 'heart', title: 'Couples', desc: 'Beginning new journeys.' },
  { icon: 'users', title: 'Families', desc: 'Creating lasting memories.' },
  { icon: 'user', title: 'Individuals', desc: 'Seeking guidance and clarity.' },
  { icon: 'globe', title: 'Communities', desc: 'Celebrating traditions together.' },
  { icon: 'lightbulb', title: 'Creators', desc: 'Building sustainable businesses.' },
  { icon: 'handshake', title: 'Partners', desc: 'Growing through opportunity.' },
];

const impacts = [
  {
    icon: 'users',
    color: '#a855f7',
    title: 'Empowered Communities',
    desc: 'We empower individuals, families and businesses to create, celebrate and grow.',
  },
  {
    icon: 'heart',
    color: '#ec4899',
    title: 'Meaningful Connections',
    desc: 'Our platforms bring people closer, strengthening relationships and building trust.',
  },
  {
    icon: 'globe',
    color: '#38bdf8',
    title: 'Sustainable Impact',
    desc: 'We are committed to building a better tomorrow through innovation, inclusion and responsibility.',
  },
];

const values = [
  {
    icon: 'users',
    color: '#ec4899',
    title: 'People First',
    desc: 'We put people at the center of everything we do.',
  },
  {
    icon: 'target',
    color: '#38bdf8',
    title: 'Purpose Driven',
    desc: 'We are driven by purpose and committed to creating real impact.',
  },
  {
    icon: 'handshake',
    color: '#22c55e',
    title: 'Built on Trust',
    desc: 'We build with integrity, transparency and mutual respect.',
  },
  {
    icon: 'trending-up',
    color: '#f59e0b',
    title: 'Future Ready',
    desc: 'We embrace innovation and build for a better tomorrow.',
  },
  {
    icon: 'heart',
    color: '#a855f7',
    title: 'Spreading Joy',
    desc: 'Joy is our foundation and the reason we keep going.',
  },
];

const brandBlurbs = {
  atlhea: 'Wedding photography and films that capture emotions and create timeless memories.',
  epanchang: 'Personalised almanac and astrology services for guidance, clarity and auspicious beginnings.',
  happiffie: 'AI-powered celebration platform with venue discovery, planning tools and seamless experiences.',
  'marriage-colours': 'Bringing people together through meaningful connections and trusted matchmaking.',
  anantmaya: 'Nurturing spiritual well-being and preserving traditions for a balanced, conscious and meaningful life.',
};

export default function Journey() {
  return (
    <div className="jr-page">
      {/* =================== HERO =================== */}
      <section className="jr-hero">
        <div
          className="jr-hero__bg"
          style={{ backgroundImage: `url(${JIMG}/Journey-1.jpeg)` }}
        />
        <div className="container container--wide jr-hero__inner">
          <div className="jr-hero__row">
            <div className="jr-hero__copy">
              <h1 className="jr-hero__title">
                Our Journey.
                <br />
                Fuelled by <span className="grad-pink">Purpose.</span>
                <br />
                Guided by <span className="grad-cyan">Joy.</span>
              </h1>
              <p className="jr-hero__lead">
                From a simple idea to a thriving ecosystem, our journey has been
                about building meaningful connections, empowering people and
                spreading joy across every moment of life.
              </p>
              <Link to="#impact" className="btn btn--outline">
                Explore Our Impact <Icon name="arrow-right" size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* =================== MILESTONES =================== */}
      <section className="jr-milestones-sec">
        <div className="container container--wide">
          <header className="section__head jr-head">
            <h2 className="h2">Milestones That Shaped Our Journey</h2>
            <span className="jr-head__line" />
          </header>

          <div className="jr-timeline">
            <div className="jr-timeline__track">
              {milestones.map((m) => (
                <div
                  className="jr-mile"
                  key={m.year}
                  style={{ '--c': m.color }}
                >
                  <span className="jr-mile__dot">
                    <Icon name={m.icon} size={24} />
                  </span>
                  <span className="jr-mile__year">{m.year}</span>
                  <h3>{m.title}</h3>
                  <p>{m.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* =================== STATS =================== */}
      <section className="jr-stats-sec">
        <div className="container container--wide">
          <div className="jr-stats">
            <h2 className="jr-stats__title">The Lives We Touch</h2>
            <div className="jr-stats__grid">
              {lives.map((s) => (
                <div className="jr-stat" key={s.title}>
                  <span className="jr-stat__icon">
                    <Icon name={s.icon} size={26} />
                  </span>
                  <div className="jr-stat__text">
                    <strong>{s.title}</strong>
                    <span>{s.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* =================== OUR BRANDS =================== */}
      <section className="jr-brands-sec">
        <div className="container container--wide">
          <header className="section__head jr-head">
            <h2 className="h2">Our Brands &ndash; The Joy We Create</h2>
            <span className="jr-head__line" />
          </header>

          <div className="jr-brand-grid">
            {brands.map((b) => (
              <article
                className="jr-brand"
                key={b.id}
                style={{ '--c': b.color }}
              >
                <div className="jr-brand__head">
                  {b.logo ? (
                    <img className="jr-brand__logo" src={b.logo} alt={b.name} />
                  ) : (
                    <span className="jr-brand__word">{b.name}</span>
                  )}
                  <span className="jr-brand__tag">{b.orbitLabel}</span>
                </div>
                <p className="jr-brand__desc">
                  {brandBlurbs[b.id] || b.blurb}
                </p>
                <a
                  href={b.website || '#brand'}
                  className="jr-brand__link"
                  target={b.website ? '_blank' : undefined}
                  rel={b.website ? 'noopener noreferrer' : undefined}
                >
                  Explore {b.name} <Icon name="arrow-right" size={14} />
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* =================== HOW FAR WE'VE COME =================== */}
      <section className="jr-impact-sec" id="impact">
        <div className="container container--wide">
          <header className="section__head jr-head">
            <h2 className="h2">How Far We&rsquo;ve Come</h2>
            <span className="jr-head__line" />
          </header>

          <div className="jr-impact">
            <div className="jr-impact__visual">
              <img src={`${JIMG}/Journey-2.jpeg`} alt="Earth from space" />
            </div>
            <div className="jr-impact__list">
              {impacts.map((i, idx) => (
                <div className="jr-impact__row" key={i.title}>
                  <span
                    className="jr-impact__icon"
                    style={{ color: i.color, borderColor: i.color }}
                  >
                    <Icon name={i.icon} size={22} />
                  </span>
                  <div>
                    <h3>{i.title}</h3>
                    <p>{i.desc}</p>
                  </div>
                  {idx < impacts.length - 1 && (
                    <span className="jr-impact__line" aria-hidden="true" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* =================== VALUES =================== */}
      <section className="jr-values-sec">
        <div className="container container--wide">
          <header className="section__head jr-head">
            <h2 className="h2">The Values That Guide Us</h2>
            <span className="jr-head__line" />
          </header>

          <div className="jr-values">
            {values.map((v) => (
              <div className="jr-value" key={v.title}>
                <span
                  className="jr-value__icon"
                  style={{ color: v.color, borderColor: v.color }}
                >
                  <Icon name={v.icon} size={30} />
                </span>
                <h3>{v.title}</h3>
                <p>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =================== CLOSING QUOTE =================== */}
      <section className="jr-quote-sec">
        <div className="container container--wide">
          <div className="jr-quote">
            <span className="jr-quote__mark">
              <Icon name="quote" size={42} />
            </span>
            <p className="jr-quote__text">
              Every step of our journey has been about one belief &mdash; that
              joy multiplies when it is shared.
            </p>
            <Link to="/contact" className="btn btn--outline">
              Be a Part of Our Journey <Icon name="arrow-right" size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
